import { useTranslation } from "react-i18next";
import styled from "styled-components";
import {
  Accordion,
  AccordionSummary,
  Box,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ConfirmDelete from "components/ConfirmDelete";
import EmailAndSource from "./EmailAndSource";
import ItemDetails from "./ItemDetails";
import { StyledAccordionDetails, StyledAvatar } from "./Styled";
import { Group } from "@dashboard-v3/api";
import EditButton from "./EditButton";
import { Alert } from "@material-ui/lab";
import { fetchGroup } from "utils/api/groups";
import useSWR from "swr";

interface ItemProps {
  group: Group;
  deleting: boolean;
  onDelete: (id: string) => void;
}

export default function Item(props: ItemProps) {
  const { t } = useTranslation("accounts");
  const { group, onDelete } = props;
  const { id, name, sourceId, members, memberGroups } = group;

  const { data: groupDetails } = useSWR(`/groups/${id}`, () => fetchGroup(id), {
    revalidateOnFocus: false,
  });

  const canEditStorageAccounts = Boolean(groupDetails?.storage);

  return (
    <ListItem disableGutters data-testid="accounts__group-list-item">
      <GroupAccordion TransitionProps={{ unmountOnExit: true }}>
        <AccordionSummary
          data-testid="accounts__group-list-item-summary"
          expandIcon={<ExpandMoreIcon />}
        >
          <ListItemIcon>
            <StyledAvatar>{name.charAt(0)}</StyledAvatar>
          </ListItemIcon>
          <ListItemText
            primary={
              <Box textOverflow="ellipsis" overflow="hidden">
                {name}
              </Box>
            }
            secondary={
              <Box display="flex" alignItems="center" flexWrap="wrap">
                <EmailAndSource group={group} />
                {!sourceId && (
                  <Box maxWidth="70%">
                    <Alert
                      severity="warning"
                      variant="standard"
                      style={{
                        padding: "0 16px",
                        marginTop: "5px",
                      }}
                    >
                      {t("noSourceId")}
                    </Alert>
                  </Box>
                )}
              </Box>
            }
          />
          <Box>
            {!sourceId && (
              <ConfirmDelete
                onAccept={() => onDelete(id)}
                message={t("buttons.confirmGroupDelete", { name })}
                variant="outlined"
              />
            )}

            {canEditStorageAccounts && (
              <Box component="span" ml="10px">
                <EditButton id={id} />
              </Box>
            )}
          </Box>
        </AccordionSummary>
        <StyledAccordionDetails style={{ display: "block" }}>
          <ItemDetails
            id={id}
            accounts={members}
            memberGroupsIds={memberGroups}
          />
        </StyledAccordionDetails>
      </GroupAccordion>
    </ListItem>
  );
}

export const GroupAccordion = styled(Accordion)`
  width: 100%;

  .MuiAccordionSummary-content {
    align-items: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;
